import type { Gallery } from '@/core/features/image-v2/gallery-type';
import type { KeyFactsType } from '@/features/key-facts-v2/key-facts-type';
import type { SchoolProductCancellation } from '@/features/school-product-tiles-v2/school-product-tiles-v2-type';

import DividerHorizontalDesktop from '@/core/components/divider/divider-horizontal-desktop';
import HtmlTextWrapper from '@/core/components/html-text-wrapper/html-text-wrapper';
import KeyFactsDesktop from '@/core/components/key-facts/key-facts-desktop';
import { Button, ModalWithMediaSwiper } from '@/core/features';
import { bemModule } from '@/core/utils/bem-classname';
import CheckoutCancellationInfoDesktop from '@/features/checkout/checkout-shared-components/checkout-cancellation-info-desktop';

import styles from './school-details-overlay-desktop.module.scss';

type SchoolDetailsOverlayDesktopProps = {
    cancellationOverlay: SchoolProductCancellation | null;
    ctaReservationButton: null | string;
    gallery: Gallery;
    htmlDescriptionText: null | string;
    keyfacts: KeyFactsType;
    onClose: () => void;
    openReservationOverlay: () => void;
    priceText: string;
    productName: string;
    schoolName: string;
    strikePriceText: null | string;
};

const bem = bemModule(styles);

export default function SchoolDetailsOverlayDesktop({
    cancellationOverlay,
    ctaReservationButton,
    gallery,
    htmlDescriptionText,
    keyfacts,
    onClose,
    openReservationOverlay,
    priceText,
    productName,
    schoolName,
    strikePriceText,
}: SchoolDetailsOverlayDesktopProps) {
    const hasNoGallery = gallery.length === 0;
    const hasReservation = ctaReservationButton !== null;
    const hasNoStrikePrice = strikePriceText === null;

    return (
        <ModalWithMediaSwiper
            gallery={gallery}
            hasSmallOverlay={true}
            imageHeight={300}
            layoutWidth={50}
            maxHeight={'68vh'}
            maxWidth={835}
            navigation={true}
            onClose={onClose}
            padding={'padding-20'}
        >
            <div className={`${bem(styles.productWrapper, { hasNoGallery })} ${styles.sticky}`}>
                <div className={styles.product}>
                    <div
                        className={bem(styles.courseName, {
                            hasNoGallery,
                        })}
                    >
                        {productName}
                    </div>
                    <div className={`padding-top-10 font-font-size-16 ${styles.schoolName}`}>{schoolName}</div>
                    {cancellationOverlay && (
                        <div className={styles.cancellationWrapper}>
                            <CheckoutCancellationInfoDesktop
                                fontSize={'font-size-14'}
                                iconUrl={cancellationOverlay.condition.iconUrl}
                                overlayHtmlText={cancellationOverlay.overlay?.htmlDescriptionText}
                                overlayHtmlTitle={cancellationOverlay.overlay?.title}
                                text={cancellationOverlay.condition.text}
                                textColor={cancellationOverlay.condition.color}
                                tippyConfig={{
                                    offset: [0, 10],
                                    placement: 'auto',
                                }}
                            />
                        </div>
                    )}

                    <div
                        className={bem(styles.priceMainWrapper, {
                            hasReservation,
                        })}
                    >
                        <div className={bem(styles.priceWrapper, { hasNoStrikePrice, hasReservation })}>
                            {strikePriceText && <div className={styles.strikePriceText}>{strikePriceText}</div>}
                            <div className={styles.priceTextWrapper}>
                                <span className={styles.startPricePrefix}>ab</span>
                                <span>{priceText}</span>
                            </div>
                        </div>
                        {ctaReservationButton && (
                            <Button
                                className={styles.ctaButton}
                                fontSize={16}
                                fontWeight={'bold'}
                                onClick={openReservationOverlay}
                                qaId={'qa-school-products-booking-overlay-button'}
                                title={ctaReservationButton}
                            />
                        )}
                    </div>
                </div>
                <DividerHorizontalDesktop
                    color={'alto'}
                    marginBottom={0}
                    marginTop={0}
                />
            </div>
            <div className={styles.descriptionWrapper}>
                <div className={'flex-1'}>
                    {keyfacts.length > 0 && (
                        <KeyFactsDesktop
                            fontSize={'font-size-16'}
                            gap={8}
                            iconSize={18}
                            keyFacts={keyfacts}
                        />
                    )}
                </div>
                <div
                    className={`${styles.descriptionTextWrapper}`}
                    data-qa-id={'qa-school-page-detail-overlay-sticky'}
                >
                    {htmlDescriptionText && (
                        <HtmlTextWrapper
                            className={styles.htmlText}
                            htmlText={htmlDescriptionText}
                        />
                    )}
                </div>
            </div>
        </ModalWithMediaSwiper>
    );
}
